import {
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    OnDestroy,
    OnInit,
    signal,
    WritableSignal
} from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    BaseFieldDefinition,
    FieldDefinition,
    FieldType
} from '../../../../core/field-definitions/entities/field-definition.entity';
import { ExtendedBaseProfileField } from '../../../../core/profiles/entities/extended-profile-field.entity';
import { UrlQueryParams } from '../../../../core/query/entities/url-query-params.entity';
import { GenerateQueryParamsFromAdvancedSearchTerm } from '../../../../core/query/helpers/query-params-generators.helper';
import { PartialRecord } from '../../../../core/records/entities/partial-record.class';
import { LabelIcon } from '../../../../form-controls/components/_core/form-label/label-icon.entity';
import { NonNullSimpleFieldValue } from '../../../entities/field-values/field-type-values.entity';
import { FieldIconHelper } from '../../../helpers/icons/field-icon.helper';
import { ShSearchRoutingService } from '../../../services/search-routing.service';

@Component({
    standalone: true,
    selector: 'sh-base-generic-readonly-field',
    template: '',
    styleUrls: ['./base-generic-readonly-field.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShBaseGenericReadonlyFieldComponent implements OnDestroy, OnInit {
    public static readonly IS_COMPLEX: boolean = false;

    public readonly destroy$: Subject<void> = new Subject<void>();

    public readonly profileField$: ReplaySubject<ExtendedBaseProfileField> =
        new ReplaySubject<ExtendedBaseProfileField>(1);

    public readonly record$: ReplaySubject<PartialRecord> = new ReplaySubject<PartialRecord>(1);
    public readonly nestedIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public nestedPadding$: Observable<Record<string, string>>;

    private labelIcons: LabelIcon[] = [];
    public $labelIcons: WritableSignal<LabelIcon[]> = signal<LabelIcon[]>([]);

    protected translateService: SharedLanguageService = inject(SharedLanguageService);
    protected readonly fieldIconHelper: FieldIconHelper = inject(FieldIconHelper);

    @Input() set profileField(extendedProfileField: ExtendedBaseProfileField) {
        this.profileField$.next(extendedProfileField);
    }

    @Input() set aiActive(aiActive: boolean) {
        if (aiActive) {
            this.labelIcons = this.fieldIconHelper.setAiIcon(this.labelIcons);
            this.$labelIcons.update(() => this.labelIcons);
        }
    }

    @Input() set record(record: PartialRecord) {
        this.record$.next(record);
    }

    @Input() set nestedIndex(nestedIndex: number) {
        this.nestedIndex$.next(nestedIndex);
    }

    @Input() index: number | undefined = undefined;

    ngOnInit(): void {
        this.setupInitListener();
        this.initNestedPadding();
    }

    constructor(protected searchRoutingService: ShSearchRoutingService) {}

    private setupInitListener(): void {
        combineLatest([this.profileField$, this.record$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([profileField, record]) => this.init(profileField, record));
    }

    protected init(profileField: ExtendedBaseProfileField, record: PartialRecord): void {}

    get searchRouterLink(): string[] {
        return this.searchRoutingService.getSearchRouterLink();
    }

    getSearchRouterParams(fieldDefinition: FieldDefinition, value: NonNullSimpleFieldValue): UrlQueryParams {
        const searchValue: string = this.requiresPreciseSearch(fieldDefinition)
            ? `"${value.toString()}"`
            : value.toString();

        return GenerateQueryParamsFromAdvancedSearchTerm(fieldDefinition.DottedKey, [searchValue]);
    }

    private requiresPreciseSearch(fieldDefinition: FieldDefinition): boolean {
        // MED-6706: Tokenised is deprecated. Only searchable TextFields are tokenised and require precise search.
        return fieldDefinition.Type === FieldType.TextField;
    }

    canSearchOnClick(fieldDefinition: BaseFieldDefinition): boolean {
        // Free-text (e.g. SimpleField) top-fields will not get search-on-click.
        if (!fieldDefinition.ParentId && [FieldType.SimpleField, FieldType.TextField].includes(fieldDefinition.Type)) {
            return false;
        }

        // MED-6327: Manual Geocoordinate search is not supported
        if (fieldDefinition.Type === FieldType.GeoCoordinateField) {
            return false;
        }

        // MED-5532: Field should be indexed to be seachable
        return fieldDefinition.Searchable;
    }

    protected initNestedPadding(): void {
        this.nestedPadding$ = this.nestedIndex$.pipe(
            map(index => {
                // Limit the max nesting to prevent UI glitches
                const nesting = Math.min(index, 3);
                return { 'padding-left': `${nesting}rem` };
            })
        );
    }

    ngOnDestroy(): void {
        this.profileField$.complete();
        this.record$.complete();
        this.nestedIndex$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }
}
